<template>
  <div class="pl-layout">
    <!-- 左侧边栏 256px -->
    <aside class="pl-sidebar">
      <!-- Logo -->
      <div class="pl-logo">
        <div class="pl-logo-circle">佩</div>
        <div class="pl-logo-text">
          <h1 class="pl-logo-title">升前研</h1>
          <p class="pl-logo-sub">项目管理</p>
        </div>
      </div>

      <!-- 导航菜单 -->
      <div class="pl-nav">
        <side-menu></side-menu>
      </div>

      <!-- 底部用户信息 -->
      <div class="pl-user">
        <div class="pl-user-inner">
          <div class="pl-avatar">
            <i class="el-icon-user"></i>
          </div>
          <div class="pl-user-info">
            <el-dropdown @command="handleUserCommand" trigger="click">
              <span class="pl-user-trigger">
                <span class="pl-user-name">{{ userName }}</span>
                <i class="el-icon-arrow-down" style="font-size:11px;margin-left:3px;"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="change-password">修改密码</el-dropdown-item>
                <el-dropdown-item command="sign-out" divided>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <p class="pl-user-account">{{ userRole || userInfo.username || '' }}</p>
          </div>
        </div>
      </div>
    </aside>

    <!-- 右侧主体 -->
    <div class="pl-main">
      <!-- 顶部 Header 64px -->
      <header class="pl-header">
        <div class="pl-header-left">
          <!-- 管理后台页面：显示页面标题 -->
          <template v-if="isAdminPage">
            <h2 class="pl-page-title">{{ $route.meta.title || '管理后台' }}</h2>
          </template>

          <!-- 项目列表页 -->
          <template v-else-if="!showProjectSelector">
            <h2 class="pl-page-title">项目列表</h2>
            <div class="pl-bc-divider"></div>
            <nav class="pl-bc-nav">
              <span class="pl-bc-item">主页</span>
              <i class="el-icon-arrow-right pl-bc-arrow"></i>
              <span class="pl-bc-current">我的项目</span>
            </nav>
          </template>

          <!-- 详情页：项目选择器 + 子菜单 -->
          <template v-if="showProjectSelector">
            <h2 class="pl-page-title">项目列表</h2>
            <div class="pl-bc-divider"></div>
            <el-dropdown @command="onProjectChange" trigger="click">
              <span class="pl-project-selector">
                项目: {{ currentProjectName }}
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown" class="pl-proj-dropdown">
                <el-dropdown-item 
                  v-for="proj in projectList" 
                  :key="proj.id" 
                  :command="proj.id"
                  :class="{ 'is-active': proj.id === currentProjectId }"
                >
                  {{ proj.name }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <div class="pl-sub-tabs">
              <router-link
                v-for="tab in projectTabs"
                :key="tab.name"
                :to="getTabPath(tab)"
                class="pl-sub-tab"
                :class="{ 'is-active': isTabActive(tab) }"
              >
                {{ tab.label }}
              </router-link>
            </div>
          </template>
        </div>

        <div class="pl-header-right">
          <!-- 管理后台页面：不显示搜索和新建 -->
          <template v-if="isAdminPage">
          </template>

          <!-- 列表页：管理后台 + 搜索 + 新建 -->
          <template v-else-if="!showProjectSelector">
            <el-button 
              v-if="isAdmin"
              size="small" 
              class="pl-admin-btn"
              @click="gotoAdmin"
            ><i class="el-icon-setting"></i> 管理后台</el-button>
            <el-input 
              v-model="listSearchName" 
              class="pl-search-input" 
              placeholder="搜索项目名称或ID..." 
              prefix-icon="el-icon-search"
              size="small"
              clearable
              @keyup.enter.native="onListSearch"
              @clear="onListSearch"
            />
            <el-button v-if="canCreateProject" type="primary" size="small" class="pl-create-btn" @click="onListCreate">
              <i class="el-icon-plus"></i> 新建项目
            </el-button>
          </template>

          <!-- 详情页：管理后台 -->
          <template v-else>
            <el-button 
              v-if="isAdmin" 
              size="small" 
              class="pl-admin-btn"
              @click="gotoAdmin"
            >
              管理后台
            </el-button>
          </template>
        </div>
      </header>

      <!-- 内容区域 -->
      <div class="pl-content">
        <el-scrollbar id="mainScroll" style="height: 100%">
          <div class="pl-content-inner" id="container">
            <keep-alive :max="4" :include="$store.state.cached">
              <router-view />
            </keep-alive>
          </div>
        </el-scrollbar>
      </div>
    </div>
  </div>
</template>

<script>
import SideMenu from './SideMenu'
import { signOut } from 'index/api'
import { getProjectList } from 'index/api/project/manager'
import { checkGuide } from '@/constants/guide'
import guideSteps from '@/constants/guide-steps-index'

export default {
  name: 'ProjectLayout',

  components: {
    SideMenu
  },

  data() {
    return {
      listSearchName: '',
      projectList: [],
      defaultTabs: [
        { name: 'overview', label: '概览', path: 'overview' },
        { name: 'forms', label: '我的问卷', path: 'forms' },
        { name: 'waves', label: '波次', path: 'waves' },
        { name: 'data', label: '数据管理', path: 'data' },
        { name: 'store-report', label: '单店报告', path: 'store-report' },
        { name: 'file-distribution', label: '报告下发', path: 'file-distribution' },
        { name: 'members', label: '成员管理', path: 'members' },
        { name: 'settings', label: '设置', path: 'settings' },
        { name: 'appeal', label: '申诉处理', path: 'appeal' }
      ]
    }
  },

  computed: {
    userInfo() {
      return this.$store.getters.userInfo || {}
    },
    userName() {
      return this.userInfo.name || this.userInfo.username || 'admin'
    },
    userRole() {
      const info = this.userInfo
      return (info.roles && info.roles.name) || ''
    },
    isAdmin() {
      const u = this.userInfo || {}
      return u.username === 'admin' || u.name === 'admin'
    },
    canCreateProject() {
      const info = this.userInfo || {}
      const sysRole = (info.roles && info.roles.name) || info.role_name || info.roleName || ''
      return this.isAdmin || sysRole === '项目管理' || sysRole === '管理员' || sysRole === '研究'
    },
    showProjectSelector() {
      return /^\/project\/\d+/.test(this.$route.path)
    },
    isAdminPage() {
      return this.$route.path.startsWith('/admin')
    },
    currentProjectId() {
      const id = this.$route.params.projectId
      return id ? parseInt(id) : null
    },
    currentProjectName() {
      if (!this.currentProjectId) return '选择项目'
      const proj = this.projectList.find(p => p.id === this.currentProjectId)
      return proj ? proj.name : '加载中...'
    },
    projectTabs() {
      const surveyRoutes = this.$store.state.surveyRoutes || []
      const projectMenu = surveyRoutes.find(r => r.uri === '/project' || r.uri === '/')
      if (projectMenu && projectMenu.children && projectMenu.children.length > 0) {
        return projectMenu.children
          .filter(item => {
            return item.is_menu === '1' && 
                   item.uri && 
                   item.uri.includes(':projectId')
          })
          .map(item => {
            const pathMatch = item.uri && item.uri.match(/\/project\/:projectId\/(.+)/)
            const path = pathMatch ? pathMatch[1] : item.uri
            return {
              name: path,
              label: item.name,
              path: path,
              uri: item.uri
            }
          })
      }
      return this.defaultTabs
    }
  },

  watch: {
    showProjectSelector: {
      immediate: true,
      handler(val) {
        if (val && this.projectList.length === 0) {
          this.loadProjects()
        }
      }
    }
  },

  mounted() {
    setTimeout(() => {
      checkGuide(this.userInfo, 'projectList', guideSteps, (guideKey, version) => {
        var token = localStorage.getItem('ACCESS_TOKEN') || ''
        var baseUrl = process.env.VUE_APP_BASE_URL || ''
        fetch(baseUrl + '/jkd/home/user/markGuide', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify({ guideKey: guideKey, guideVersion: version })
        }).catch(function () {})
      }, this.$router)
    }, 1000)
  },

  methods: {
    async loadProjects() {
      try {
        const res = await getProjectList({ page: 1, pageSize: 100 })
        const records = res.data?.data?.records || res.data?.records || []
        this.projectList = records
      } catch (e) {
        const records = e?.data?.data?.records || e?.data?.records
        if (records && records.length > 0) {
          this.projectList = records
        } else {
          console.error('加载项目列表失败', e)
        }
      }
    },
    
    getTabPath(tab) {
      return `/project/${this.currentProjectId}/${tab.path}`
    },
    
    isTabActive(tab) {
      const path = this.$route.path
      return path.includes(`/${tab.path}`) || 
             (tab.name === 'overview' && path.match(/^\/project\/\d+$/))
    },
    
    onProjectChange(projectId) {
      if (projectId !== this.currentProjectId) {
        const firstPath = this.projectTabs.length > 0 ? this.projectTabs[0].path : 'overview'
        this.$router.push(`/project/${projectId}/${firstPath}`)
      }
    },
    
    handleUserCommand(command) {
      switch (command) {
        case 'change-password':
          this.$router.push('/change-password')
          break
        case 'sign-out':
          signOut().then(() => {
            localStorage.removeItem('ACCESS_TOKEN')
            window.location.href = '/'
          }).finally(() => {
            localStorage.removeItem('ACCESS_TOKEN')
            window.location.href = '/'
          })
          break
      }
    },
    
    gotoAdmin() {
      window.location.href = '/admin'
    },
    
    // 列表页搜索 - 通过事件总线通知 List.vue
    onListSearch() {
      this.$root.$emit('project-list-search', this.listSearchName)
    },
    
    // 列表页新建 - 通过事件总线通知 List.vue
    onListCreate() {
      this.$root.$emit('project-list-create')
    }
  }
}
</script>

<!-- 非 scoped 样式 — 允许穿透到 SideMenu/treeMenu 子组件 -->
<style lang="scss">
/* ============================================
   ProjectLayout — 匹配原型图设计
   ============================================ */

/* 整体布局 */
.pl-layout {
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background: #f8fafc;
}

/* ==================== 侧边栏 ==================== */
.pl-sidebar {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  flex-shrink: 0;
  background: #fff;
  border-right: none;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.06);
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Logo */
.pl-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  flex-shrink: 0;
}

.pl-logo-circle {
  width: 40px;
  height: 40px;
  background: #e53935;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
}

.pl-logo-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.pl-logo-sub {
  font-size: 11px;
  color: #94a3b8;
  margin: 3px 0 0;
}

/* 导航 */
.pl-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: 0;
}

/* 覆盖 SideMenu / treeMenu 样式 */
.pl-nav .side-menu {
  padding: 0 16px !important;
  margin-top: 0 !important;
  background: transparent !important;
  width: 100% !important;
  box-sizing: border-box;
}

.pl-nav .menuList {
  list-style: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

.pl-nav .menuList .menu-item {
  padding: 2px 0 !important;
  line-height: 1 !important;
  font-weight: normal !important;
}

.pl-nav .menuList .menu-item__wrap {
  display: flex !important;
  align-items: center !important;
  padding: 10px 12px !important;
  border-radius: 8px !important;
  color: #475569 !important;
  font-size: 13px !important;
  transition: all 0.15s ease !important;
  cursor: pointer !important;
}

.pl-nav .menuList .menu-item__wrap:hover {
  background: #f1f5f9 !important;
  color: #0f172a !important;
}

.pl-nav .menuList .menu-item.active > .menu-item__wrap {
  background: rgba(20, 118, 255, 0.08) !important;
  color: #1476ff !important;
  font-weight: 500 !important;
}

.pl-nav .menuList .menu-icon {
  float: none !important;
  width: auto !important;
  font-size: 18px !important;
  margin-right: 12px !important;
  flex-shrink: 0;
}

.pl-nav .menuList .menu-name {
  margin-left: 0 !important;
  font-size: 13px !important;
}

/* 底部用户 */
.pl-user {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.pl-user-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
}

.pl-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #94a3b8;
  font-size: 18px;
}

.pl-user-info {
  flex: 1;
  min-width: 0;
}

.pl-user-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.pl-user-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
}

.pl-user-account {
  font-size: 11px;
  color: #94a3b8;
  margin: 2px 0 0;
  line-height: 1;
}

/* ==================== 右侧主体 ==================== */
.pl-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* Header */
.pl-header {
  height: 56px;
  min-height: 56px;
  flex-shrink: 0;
  background: #fff;
  border-bottom: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  z-index: 5;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.pl-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.pl-page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  white-space: nowrap;
}

.pl-bc-divider {
  width: 1px;
  height: 16px;
  background: #cbd5e1;
  margin: 0 8px;
}

.pl-bc-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pl-bc-item {
  font-size: 13px;
  color: #94a3b8;
}

.pl-bc-arrow {
  font-size: 11px;
  color: #94a3b8;
}

.pl-bc-current {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}

.pl-search-input {
  width: 220px;

  .el-input__inner {
    background: #f1f5f9;
    border: none;
    border-radius: 8px;
    font-size: 12px;
  }
  
  .el-input__inner:focus {
    box-shadow: 0 0 0 2px rgba(20, 118, 255, 0.15);
  }
}

.pl-admin-btn {
  border-radius: 8px !important;
  font-size: 13px !important;
  height: 32px !important;
  line-height: 1 !important;
  padding: 0 14px !important;
  color: #475569 !important;
  border-color: #e2e8f0 !important;
  background: #fff !important;

  &:hover {
    color: #1476ff !important;
    border-color: #1476ff !important;
  }
}

.pl-create-btn {
  background: #1476ff !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600;
}

.pl-project-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #1476ff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.pl-proj-dropdown {
  max-height: 300px;
  overflow-y: auto;
  
  .is-active {
    color: #1476ff;
    font-weight: 500;
  }
}

/* 子菜单 Tab */
.pl-sub-tabs {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid #e2e8f0;
}

.pl-sub-tab {
  padding: 5px 12px;
  font-size: 13px;
  color: #64748b;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.15s;
  white-space: nowrap;
  
  &:hover {
    color: #1476ff;
    background: #f1f5f9;
  }
  
  &.is-active {
    color: #1476ff;
    font-weight: 500;
    background: rgba(20, 118, 255, 0.08);
  }
}

/* Header 右侧 */
.pl-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.pl-admin-btn {
  border-radius: 6px !important;
  font-size: 12px !important;
}

/* 内容区域 */
.pl-content {
  flex: 1;
  overflow: hidden;
  background: #f8fafc;
}

.pl-content-inner {
  padding: 20px 24px;
  min-height: 100%;
}

.pl-content .el-scrollbar__wrap {
  overflow-x: hidden;
}
</style>
